/**
 * Application monitoring via Amazon CloudWatch RUM (Real User Monitoring).
 *
 * This module replaces the previous Bugsnag integration. It wraps the
 * `aws-rum-web` client behind a small, Bugsnag-flavoured API so the rest of the
 * codebase can report errors, leave breadcrumbs, identify the current user and
 * record custom performance spans without depending on a specific vendor.
 *
 * Configuration is provided at build time through Vite environment variables
 * (see `vite-env.d.ts`). When the required variables are missing - which is the
 * case for local development and tests - the module becomes a no-op so nothing
 * is sent to AWS.
 *
 * Infrastructure setup (CloudWatch RUM app monitor, Cognito identity pool, IAM
 * role) is documented in `docs/cloudwatch-rum-setup.md`.
 */

// NOTE: we use the full `aws-rum-web` entry rather than `@aws-rum/web-slim`.
// The slim build excludes the rrweb (session-replay) subtree, but it also drops
// the built-in Cognito auth, SigV4 signing and telemetry-plugin system, so it
// would require manually wiring credentials and each plugin (and pulling in
// @aws-sdk/credential-providers). Not worth the trade-off here - aws-rum-web's
// Orchestration statically instantiates RRWebPlugin so rrweb is bundled
// regardless of the telemetries config, but the convenience of built-in auth +
// telemetries outweighs that weight for this migration.
import { AwsRum, type AwsRumConfig } from 'aws-rum-web';
import { isIgnorableNetworkError } from './network-error-patterns.js';
import type { MonitoringSpan, NotifyMetadata, NotifyOptions } from './monitoring-types.js';

let rum: AwsRum | undefined;
let initialized = false;

// Mirror custom events and breadcrumbs to the console during local development,
// where RUM usually isn't configured. This replaces ad-hoc console.log calls at
// the call sites: report through the facade, and dev builds still show a trail.
const mirrorToConsole = import.meta.env.DEV;

/**
 * Collapse high-cardinality URLs (organization ids, UUIDs) into stable route
 * templates so RUM page-view metrics group sensibly. This previously fed
 * Bugsnag's performance routing provider.
 */
export function resolveRoute(pathname: string): string {
  // /account/:organizationId/:pageId/:entityType1/:uuid1/:entityType2/:uuid2/:entityType3/:uuid3
  const level3Match = pathname.match(/^\/account\/(\d+)\/(\d+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)$/);
  if (level3Match) {
    const [, , pageId, entityType1, , entityType2, , entityType3] = level3Match;
    return `/account/:organizationId/${pageId}/${entityType1}/:uuid/${entityType2}/:uuid/${entityType3}/:uuid`;
  }

  // /account/:organizationId/:pageId/:entityType1/:uuid1/:entityType2/:uuid2
  const level2Match = pathname.match(/^\/account\/(\d+)\/(\d+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)$/);
  if (level2Match) {
    const [, , pageId, entityType1, , entityType2] = level2Match;
    return `/account/:organizationId/${pageId}/${entityType1}/:uuid/${entityType2}/:uuid`;
  }

  // /account/:organizationId/:pageId/:entityType1/:uuid1
  const level1Match = pathname.match(/^\/account\/(\d+)\/(\d+)\/([^/]+)\/([^/]+)$/);
  if (level1Match) {
    const [, , pageId, entityType1] = level1Match;
    return `/account/:organizationId/${pageId}/${entityType1}/:uuid`;
  }

  // /account/:organizationId/:pageId
  const pageMatch = pathname.match(/^\/account\/(\d+)\/(\d+)$/);
  if (pageMatch) {
    const [, , pageId] = pageMatch;
    return `/account/:organizationId/${pageId}`;
  }

  // /account/:organizationId
  if (pathname.match(/^\/account\/\d+$/)) {
    return '/account/:organizationId';
  }

  // /account/:organizationId/issue-messages/:issueId (melderens meldingsside)
  if (pathname.match(/^\/account\/\d+\/issue-messages\/[^/]+$/)) {
    return '/account/:organizationId/issue-messages/:issueId';
  }

  // Static routes
  if (pathname === '/account') return '/account';
  if (pathname === '/signin') return '/signin';
  if (pathname === '/signup') return '/signup';
  if (pathname === '/dashboard') return '/dashboard';
  if (pathname === '/') return '/';

  return pathname;
}

/**
 * Sanitize an API URL for use in monitoring events: strip the query string
 * (may carry search terms or tokens) and collapse high-cardinality/PII path
 * segments — numeric ids, UUIDs and account emails — into placeholders so
 * events group per endpoint in CloudWatch Logs Insights.
 */
export function sanitizeUrlForMonitoring(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    const path = parsed.pathname
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':uuid')
      // Account endpoints carry the email in the path (/accounts/<email>/accountStatus).
      .replace(/\/[^/]*(?:@|%40)[^/]*/g, '/:email')
      .replace(/\/\d+(?=\/|$)/g, '/:id');
    return parsed.origin + path;
  } catch {
    return url.split('?')[0];
  }
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

/**
 * Decide whether an error is worth reporting. This preserves the noise filters
 * that previously lived in the Bugsnag `onError` callback in `app-root.ts`:
 * network blips, expected user-facing HTTP errors, session expirations and Ably
 * connection churn are all dropped.
 */
export function shouldReportError(error: Error, metadata?: NotifyMetadata): boolean {
  const errorClass = error.name || '';
  const errorMessage = (error.message || '').toLowerCase();
  const stack = error.stack || '';

  // Expected when users navigate away mid-render.
  if (errorClass === 'RenderingCancelledException') return false;

  // Network blips (Failed to fetch / Load failed / aborted / FetchError wrapper).
  // isIgnorableNetworkError inspects both the error and its `cause`.
  if (isIgnorableNetworkError(error)) return false;

  // ResponseError: only report unexpected statuses. 401/403/404 and "no status"
  // are normal user-facing flows, not bugs.
  if (errorClass === 'ResponseError') {
    const responseError = error as unknown as {
      status?: number;
      response?: { status?: number };
    };
    const statusFromMessage = (() => {
      // Context-qualified so we don't mistake an arbitrary 3-digit number in the
      // message (an id, a count) for an HTTP status. errorMessage is already
      // lower-cased, so no /i flag is needed.
      const match = errorMessage.match(/(?:status|code|http)[:\s]+(\d{3})/);
      return match ? parseInt(match[1], 10) : undefined;
    })();
    const status =
      responseError.status ??
      responseError.response?.status ??
      (metadata?.response?.status as number | undefined) ??
      (metadata?.entity?.httpStatus as number | undefined) ??
      (metadata?.http?.status as number | undefined) ??
      statusFromMessage;

    if (status === undefined) return false;
    if (status === 401 || status === 403 || status === 404) return false;
    return true;
  }

  // Expected session expiration.
  if (errorClass === 'RefreshTokenExpiredError') return false;
  if (errorClass === 'Error' && errorMessage.includes('refresh token is expired')) return false;

  // Ably connection / channel churn - infrastructure, not application bugs.
  // Scoped to errorClass === 'Error' (as in the original Bugsnag onError filter)
  // so a real TypeError/ResponseError mentioning "connection failed" is not
  // silently dropped.
  if (errorClass === 'Error') {
    const ablyConnectionPatterns = [
      'connection closed',
      'connection failed',
      'connection to ably is closing',
      'connection to ably was disconnected',
      'channel denied access',
      'channel denied access based on given capability',
      'unable to recover connection',
    ];
    if (ablyConnectionPatterns.some((pattern) => errorMessage.includes(pattern))) return false;

    const ablyErrorCodes = [40160]; // capability denied
    const errorCode = errorMessage.match(/code[:\s]+(\d+)/)?.[1];
    if (errorCode && ablyErrorCodes.includes(parseInt(errorCode, 10))) return false;
  }

  const fromAblySdk = stack.includes('ably.js') || stack.includes('ably-node.js') || stack.includes('ably.min.js');
  if (fromAblySdk && errorMessage.includes('connection')) return false;

  return true;
}

/**
 * Resolve the session sample rate from its raw env string. Defaults to 1
 * (sample everything) for an empty/missing value (the Dockerfile sets the ENV to
 * '' when the build arg is omitted) or a malformed value ('abc' -> NaN). Note
 * the string '0' is truthy in JS, so an explicit '0' is parsed and honoured as
 * "disable sampling".
 */
export function resolveSampleRate(raw: string | undefined): number {
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : 1;
}

/**
 * Initialise CloudWatch RUM. Safe to call once at app startup. Does nothing when
 * the build is not configured with RUM credentials (local dev, tests) or when
 * monitoring has already been initialised.
 */
export function initMonitoring(): void {
  if (initialized) return;

  const applicationId = import.meta.env.VITE_RUM_APPLICATION_ID;
  const identityPoolId = import.meta.env.VITE_RUM_IDENTITY_POOL_ID;
  const region = import.meta.env.VITE_RUM_REGION;

  if (!applicationId || !identityPoolId || !region) {
    // Definitively disabled for this session - no point retrying.
    initialized = true;
    console.info('[Monitoring] CloudWatch RUM not configured - monitoring disabled.');
    return;
  }

  const endpoint = import.meta.env.VITE_RUM_ENDPOINT || `https://dataplane.rum.${region}.amazonaws.com`;
  const sessionSampleRate = resolveSampleRate(import.meta.env.VITE_RUM_SESSION_SAMPLE_RATE);

  try {
    const config: AwsRumConfig = {
      sessionSampleRate,
      identityPoolId,
      endpoint,
      // We do NOT enable the 'errors' telemetry: uncaught errors are routed
      // through notifyError() so the noise filters in shouldReportError() apply.
      telemetries: ['performance', 'http'],
      allowCookies: true,
      enableXRay: false,
      // Auto page views would record raw URLs (organization ids, UUIDs); we
      // record parameterised routes via recordPageView() instead.
      disableAutoPageView: true,
      // Compression (on by default in 3.x) pipes the payload through a blob +
      // CompressionStream; Safari fails the blob load ("access control
      // checks"), the dispatch rejects and the whole PutRumEvents batch is
      // lost - RUM goes blind for exactly the Safari sessions we need
      // (issue #13437). Our payloads are small; skip compression.
      compressionStrategy: { enabled: false },
      // We deliberately do NOT pass guestRoleArn. Providing it makes aws-rum-web
      // use Cognito's basic/classic flow (GetId → GetOpenIdToken →
      // AssumeRoleWithWebIdentity), which the identity pool rejects with
      // "Basic (classic) flow is not enabled, please use enhanced flow". Without
      // it, the client uses the enhanced flow (GetCredentialsForIdentity) and
      // Cognito hands out credentials for the role attached to the pool.
    };

    rum = new AwsRum(applicationId, __APP_VERSION__, region, config);
    // Mark initialised only after the constructor succeeds. If it throws, the
    // flag stays false so a later call can retry instead of silently no-opping
    // for the whole session.
    initialized = true;

    // Record the initial route.
    recordPageView();

    // Capture uncaught errors ourselves so the filters apply (the auto 'errors'
    // telemetry above is intentionally disabled). Unhandled promise rejections
    // are routed via setupNetworkErrorHandlers() in network-error-handler.ts.
    window.addEventListener('error', (event: ErrorEvent) => {
      if (event.error) notifyError(event.error);
    });

    // CSP violations never surface as JS errors, but they break features
    // silently (e.g. Safari blocking blob: workers, issue #13413). blockedURI
    // is origin/scheme-only per the CSP spec, but strip any query defensively.
    document.addEventListener('securitypolicyviolation', (event: SecurityPolicyViolationEvent) => {
      recordEvent('dabih_csp_violation', {
        violatedDirective: event.violatedDirective,
        effectiveDirective: event.effectiveDirective,
        blockedURI: (event.blockedURI || '').split('?')[0],
        sourceFile: (event.sourceFile || '').split('?')[0],
        disposition: event.disposition,
      });
    });

    console.info('[Monitoring] CloudWatch RUM initialised.');
  } catch (error) {
    console.error('[Monitoring] Failed to initialise CloudWatch RUM:', error);
  }
}

/**
 * Report an error to CloudWatch RUM after applying the noise filters. Optional
 * metadata is attached as a queryable custom event.
 */
export function notifyError(error: unknown, options?: NotifyOptions): void {
  const err = toError(error);
  if (!shouldReportError(err, options?.metadata)) {
    console.warn('[Monitoring] Error filtered (not reported):', err.name, err.message);
    return;
  }

  if (!rum) return;

  try {
    // Record the error first - it is the primary signal. The supplementary
    // context event is emitted afterwards so a recordError failure can never
    // leave an orphaned context event with no matching error.
    rum.recordError(err);
    if (options?.metadata || options?.context || options?.severity) {
      rum.recordEvent('dabih_error_context', {
        name: err.name,
        message: err.message,
        ...(options.severity ? { severity: options.severity } : {}),
        ...(options.context ? { context: options.context } : {}),
        // Sections (entity, http, response, …) are spread as nested objects so
        // CloudWatch Logs Insights can query them as `entity.type` without
        // backtick-escaping dot-in-key fields. Section names don't collide with
        // name/message/severity/context.
        ...(options.metadata ?? {}),
      });
    }
  } catch (reportingError) {
    console.error('[Monitoring] Failed to record error:', reportingError);
  }
}

/**
 * Record a custom RUM event. Use for telemetry signals that are not errors
 * (retry rates, timings) or that must bypass the shouldReportError noise
 * filter deliberately (e.g. exhausted-retry fetch failures, whose message
 * matches the ignorable network patterns by design). Keep the volume low:
 * record exceptions and milestones, never per successful call.
 */
export function recordEvent(name: string, attributes: Record<string, unknown>): void {
  if (mirrorToConsole) console.debug('[Monitoring]', name, attributes);
  if (!rum) return;
  try {
    rum.recordEvent(name, attributes);
  } catch (error) {
    console.error('[Monitoring] Failed to record event:', error);
  }
}

/**
 * Leave a breadcrumb. CloudWatch RUM has no native breadcrumb concept, so these
 * are recorded as custom events that can be correlated with later errors in the
 * same session.
 */
export function leaveBreadcrumb(message: string, metadata?: Record<string, unknown>): void {
  if (mirrorToConsole) console.debug('[Monitoring] breadcrumb:', message, metadata ?? '');
  if (!rum) return;
  try {
    rum.recordEvent('dabih_breadcrumb', {
      // Spread first so a `message` key in metadata cannot clobber the breadcrumb message.
      ...(metadata ?? {}),
      message,
    });
  } catch (error) {
    console.error('[Monitoring] Failed to leave breadcrumb:', error);
  }
}

/**
 * Identify (or clear) the current user. Maps to RUM session attributes. Call
 * with no arguments to clear the identity (e.g. on logout).
 */
export function setMonitoringUser(id?: string, email?: string, name?: string): void {
  if (!rum) return;
  try {
    rum.addSessionAttributes({
      userId: id ?? '',
      userEmail: email ?? '',
      userName: name ?? '',
    });
  } catch (error) {
    console.error('[Monitoring] Failed to set user:', error);
  }
}

/**
 * Record a page view for the current location, collapsed to a stable route
 * template. Call on every client-side navigation.
 */
export function recordPageView(): void {
  if (!rum) return;
  try {
    rum.recordPageView(resolveRoute(window.location.pathname));
  } catch (error) {
    console.error('[Monitoring] Failed to record page view:', error);
  }
}

/**
 * Start a custom performance span. Replaces BugsnagPerformance.startSpan. The
 * span is recorded as a RUM custom event with its duration when ended.
 */
export function startSpan(name: string): MonitoringSpan {
  const start = performance.now();
  const attributes: Record<string, string | number | boolean> = {};
  let ended = false;

  return {
    setAttribute(key: string, value: string | number | boolean): void {
      attributes[key] = value;
    },
    end(): void {
      if (ended) return;
      ended = true;
      if (!rum) return;
      try {
        rum.recordEvent('dabih_span', {
          name,
          durationMs: performance.now() - start,
          ...attributes,
        });
      } catch (error) {
        console.error('[Monitoring] Failed to record span:', error);
      }
    },
  };
}
